import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, SlidersHorizontal, RefreshCw } from 'lucide-react';
import { getBrokers } from '../lib/api';
import { HomeHero } from '../components/layout/HomeHero';
import { RiskWarningBanner } from '../components/layout/RiskWarningBanner';
import { useIsMobile } from '../hooks/useIsMobile';
import { CampaignBrokerCard } from '../components/broker/CampaignBrokerCard';
import { CampaignNotices } from '../components/broker/CampaignNotices';
import { SEO, buildComparisonSchema } from '../components/SEO';
import { Button } from '../components/ui/button';
import './CampaignComparePage.css';

export default function CampaignComparePage({ campaign }) {
  const isMobile = useIsMobile();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getBrokers()
      .then(({ data }) => { if (active) setBrokers(data); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [attempt]);

  const featured = brokers.find(({ slug }) => slug === campaign.slug);
  // Only the first position changes. Preserve the admin-defined order for peers.
  const ordered = featured ? [featured, ...brokers.filter(({ slug }) => slug !== campaign.slug)] : [];
  const includesRoboForex = ordered.some(({ slug }) => slug === 'roboforex');
  const unavailable = error || (!loading && !featured);
  const schema = { ...buildComparisonSchema(ordered), description: 'Broker conditions comparison. Featured placement is promotional, not a quality ranking.' };

  return (
    <div className="campaign-page" data-testid="campaign-compare-page" data-campaign={campaign.slug}>
      <SEO title={`${campaign.name} & Other Brokers Compared`} canonical="/" robots="noindex, follow"
        description={`Compare ${campaign.name} alongside other Forex and CFD brokers: regulation, deposits, spreads, leverage and platforms.`}
        jsonLd={!loading && !unavailable ? schema : undefined} />
      {!isMobile && <><RiskWarningBanner /><HomeHero campaign /></>}
      <section id="broker-comparison" className="campaign-comparison" aria-labelledby="campaign-heading" data-testid="campaign-comparison-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="campaign-section-heading">
            <span className="campaign-eyebrow" data-testid="campaign-section-label"><SlidersHorizontal size={14} aria-hidden="true" /> Broker comparison</span>
            <h2 id="campaign-heading" className="text-base md:text-lg font-semibold" data-testid="campaign-heading">Forex & CFD Brokers Compared</h2>
            <p data-testid="campaign-introduction">Compare regulation, trading costs and platforms.</p>
          </header>
          {loading ? (
            <div role="status" className="campaign-status" data-testid="campaign-loading">Loading broker comparison…</div>
          ) : unavailable ? (
            <div role="alert" className="campaign-status" data-testid="campaign-error">
              <p>{error ? 'Unable to load the comparison. Please try again.' : 'This broker comparison is currently unavailable.'}</p>
              <Button onClick={() => setAttempt(value => value + 1)} className="mt-4" data-testid="campaign-retry"><RefreshCw aria-hidden="true" /> Try again</Button>
            </div>
          ) : (
            <>
              <ol className="campaign-broker-list" aria-label="Broker comparison, featured placement first" data-testid="campaign-broker-list">
                {ordered.map((broker, index) => <CampaignBrokerCard key={broker.slug} broker={broker} featured={index === 0} position={index + 1} />)}
              </ol>
              <CampaignNotices includesRoboForex={includesRoboForex} />
            </>
          )}
          <Link to="/compare" className="campaign-detail-link" data-testid="campaign-compare-details">Compare all brokers in detail <ArrowRight size={16} aria-hidden="true" /></Link>
        </div>
      </section>
      {isMobile && <><HomeHero campaign /><RiskWarningBanner /></>}
    </div>
  );
}